import { env } from '@/config/env';
import {
  createClient,
  createComputeScope,
  createPublicClient,
  registerHttpClient,
  withImpersonation,
  withProjectOnCreate,
} from '@/lib/http';

// The one place the app creates and registers its HTTP clients. Registration order inside a
// client is behaviour (axios runs request interceptors last-registered-first): keep it.
// `src/api/client.ts` imports this file, so the registry is full before anything asks it.

/** Ready-to-use API clients, one per backend surface. (DataCentral = v4.) */
registerHttpClient('dataCentralApi', createClient(env.dataCentralApiUrl));
registerHttpClient('serviceApi', createClient(env.serviceApiUrl));
registerHttpClient('serviceApiV3', createClient(env.serviceApiV3Url, {
  requestInterceptors: [withImpersonation],
}));

/**
 * margarita-foundation clients. Cut over surface by surface (organization,
 * impersonation, reconciliation first); unset APP_FOUNDATION_API_URL → @mgmt v3,
 * which serves the same paths, so rollback is an env change.
 */
const foundationBaseUrl = env.foundationApiUrl || env.serviceApiV3Url;
registerHttpClient('foundationApi', createClient(foundationBaseUrl, {
  requestInterceptors: [withImpersonation],
}));
/** Unauthenticated foundation client — e.g. the token-gated reconciliation resolve. */
registerHttpClient('foundationApiPublic', createPublicClient(foundationBaseUrl));

/**
 * @usagez read API client — per-service CDN + media (LMS/VMS) usage, called
 * directly (same JWT-injection model as the other clients). @usagez verifies the
 * partner-realm token via JWKS and resolves the caller's org server-side, so no
 * tenant/serviceId leakage beyond the path :id (a margarita service uuid).
 */
registerHttpClient('usagezApi', createClient(env.usagezApiUrl));

/**
 * FastChannel (FAST playout) client — the service_uuid-scoped v2 control plane
 * (channels/schedules/storages).
 */
registerHttpClient('fastChannelApi', createClient(env.fastChannelApiUrl));

/**
 * Tiger (managed SSL) client — the @tiger controlplane cert inventory, called
 * directly. Base includes /api/v1; JWT-scoped, tenant implicit from the org
 * claim (no tenant/serviceId in any path).
 */
registerHttpClient('tigerApi', createClient(env.tigerApiUrl));

/**
 * Compute (OpenNebula) client — the @compute controlplane, called directly, the
 * same way tiger and the DRM control plane are. Base includes /api/v3.
 *
 * Compute verifies the JWT and resolves the tenant itself, so @mgmt is not
 * needed in front of it. What it does still need is which subscribed service a
 * request is about, which no tenant claim carries — it reads the provider
 * metadata id returned by the service-detail endpoint from the
 * `X-Compute-Metadata-ID` header.
 *
 * Every compute call is built by `onPath(serviceId, …)`, which writes the
 * service UUID into the path because that is the shape @mgmt proxies. Rather
 * than rewrite a hundred call sites, the compute scope's request interceptor
 * (`@/lib/http/compute-scope`) resolves that UUID to its metadata id, lifts it
 * into the header, and leaves the direct service the plain `/instances` it
 * actually serves.
 */
const computeScope = createComputeScope();
registerHttpClient(
  'computeApi',
  createClient(env.computeApiUrl, {
    requestInterceptors: [computeScope.interceptor, withProjectOnCreate(/\/instances$/)],
  }),
);

/** Tell the compute client which provider metadata id a subscribed service resolves to. */
export const setComputeMetadataID = computeScope.setMetadataID;

/**
 * Healthcheck & Alerting (monitoring) client. Dedicated Go service; base already
 * includes /api/v1 (watchpoints, alarms, channels, alarm-events, playbooks).
 */
registerHttpClient('healthcheckApi', createClient(env.healthcheckApiUrl));

/**
 * Managed Kubernetes v2 (margarita/kubernetes) client. Dedicated Go service called
 * directly; base already includes /api/v2/kubernetes. JWT-scoped — the tenant is
 * resolved server-side from the org claim, so NO serviceId appears in any path.
 * Distinct from the v1 s10015 console, which goes through serviceApiV3. Creating a cluster takes the
 * working project (`X-Project-ID`, `withProjectOnCreate`); nothing else does.
 */
registerHttpClient('k8sV2Api', createClient(env.k8sV2ApiUrl, {
  requestInterceptors: [withProjectOnCreate(/^\/clusters$/)],
}));

/**
 * Media Transcode Service (@media gateway) client. Called directly like tiger/
 * control; base already includes /api/v1. Onboarding via /workspace; tenant
 * resources proxied under /tenant/* to the caller's bound vStream tenant. Tenant is
 * implicit from the JWT org claim — no tenant/serviceId in any path.
 */
registerHttpClient('mediaApi', createClient(env.mediaApiUrl));

/**
 * Raven CDN (margarita-raven) client. Called directly; base already includes /api/v1. The
 * tenant is resolved server-side from the JWT, and a domain is addressed by its registry id
 * or its name. Unset APP_RAVEN_API_URL → the Raven module keeps using @mgmt (serviceApiV3):
 * see `src/modules/Raven/services/config.ts`, so rollback is an env change.
 */
registerHttpClient('ravenApi', createClient(env.ravenApiUrl));

/**
 * Eagle CDN (margarita-flashcone) client. Called directly; base already includes /api/v1. The
 * tenant is resolved server-side from the JWT; a domain is addressed by its registry id under its
 * service: `/services/:serviceId/domains/:domainId/...`. Unlike Raven there is no @mgmt fallback
 * (the controlplane has no equivalent read), so the Eagle module says "not configured" when
 * APP_EAGLE_API_URL is unset.
 */
registerHttpClient('eagleApi', createClient(env.eagleApiUrl));

/**
 * WAAP on margarita-flashcone (HMAC routes under /services/:serviceId/waap/websites/:websiteId/...).
 * Same host as Eagle but its own variable, so rolling WAAP back never touches Eagle. Base includes
 * /api/v1; the tenant is resolved server-side from the JWT. Read paths only for now; unset
 * APP_WAAP_FLASHCONE_API_URL -> `packages/waap` keeps using @mgmt (serviceApiV3), so rollback is an env
 * change. No `withImpersonation`: `X-Impersonation-Token` is an @mgmt concept and flashcone's CORS
 * does not allow it.
 */
registerHttpClient('waapFlashconeApi', createClient(env.waapFlashconeApiUrl));

/**
 * Business Mail (margarita/mail) client. Called directly; base already includes
 * /api/v3. Activation via /workspace; the organization comes from the JWT, so
 * no tenant/serviceId ever appears in a path.
 */
registerHttpClient('mailApi', createClient(env.mailApiUrl));

/**
 * Hosting (margarita/weaver) client. Called directly; base already includes /api/v3. Activation via
 * /workspace; the organization comes from the JWT, so no tenant/serviceId ever appears in a path.
 */
registerHttpClient('hostingApi', createClient(env.hostingApiUrl));

/**
 * DRM control plane (sig-control) client. Multi-tenant Go service called
 * DIRECTLY by the portal — same JWT-injection model as healthcheckApi. Base
 * already includes the versioned API prefix /api/v1 (e.g.
 * https://sig.control.vnetwork.dev/api/v1). Tenant is implicit: resolved
 * server-side from the JWT org claim (ownership), so NO tenant/serviceId ever
 * appears in a path. Every surface (streams, provision, usage, sessions, logs)
 * is owner-scoped. See packages/aes/src/services/streams.ts.
 */
registerHttpClient('controlApi', createClient(env.controlApiUrl));

// Streams, usage, sessions, and logs are now served DIRECTLY by the DRM control
// plane (controlApi, /api/v1/*, tenant from JWT). Only /metricsz + /healthz —
// @aes data-plane runtime — are still reached through @mgmt (serviceApiV3), which
// injects the gateway's ADMIN_API_KEY server-side. See packages/aes/src/services/.

/** Authenticated client on the auth service (e.g. /auth/v2/user_info). */
registerHttpClient('authApiAuthed', createClient(env.authUrl));

/** Unauthenticated clients for the auth/login flow. */
registerHttpClient('authApi', createPublicClient(env.authUrl));
registerHttpClient('dataCentralPublic', createPublicClient(env.dataCentralApiUrl));
